/**
 * Approval routing C1: inbox, per-item request history, decisions, routing preview and the
 * admin flow/settings/cover screens. Backend: app/django-core/apps/approvals/ (views.py, urls.py).
 *
 * Item-side actions (submit, resubmit) stay in the governance/compliance entities.
 * Approver picks are keyed by FLOW step order (ruling R5); escalation levels use role_id/user_id (R11).
 * `requests/` and `eligible/` answer 404 when the caller may not see the item (R14, R15).
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { governanceApi } from '../../governance/api/governanceApi';
import { complianceApi } from '../../compliance/api/complianceApi';
import { ethicsApi } from '../../ethics/api/ethicsApi';
import { riskApi } from '../../risk/api/riskApi';
import { techManagementApi } from '../../tech-management/api/techManagementApi';
import { aiGovernanceApi } from '../../ai-governance/api/aiGovernanceApi';
import { auditApi } from '../../audit/api/auditApi';
import { auditTrailApi } from '../../../shared/api/auditTrailApi';

const BASE = '/api/v1/approvals';

// Which item-side cache a flow's list/detail lives in.
const ITEM_TAGS = {
  'governance.policy': () => governanceApi.util.invalidateTags(['GovernanceFramework']),
  'compliance.exception': () => complianceApi.util.invalidateTags(['Exception']),
  'compliance.dpia': () => complianceApi.util.invalidateTags(['DPIA']),
  // C2 flows (base keys: requests are stored under them, ruling C2-R9).
  'ethics.gift': () => ethicsApi.util.invalidateTags(['GiftDeclaration']),
  'ethics.coi': () => ethicsApi.util.invalidateTags(['ConflictOfInterest']),
  'ethics.code_of_conduct': () => ethicsApi.util.invalidateTags(['CodeOfConduct']),
  'risk.appetite': () => riskApi.util.invalidateTags(['RiskAppetite']),
  'tech.change': () => techManagementApi.util.invalidateTags(['ChangeRequest', 'ChangeApproval']),
  // An approved assessment also sets the vendor's rating and next review date.
  'tech.vendor_assessment': () =>
    techManagementApi.util.invalidateTags(['VendorRiskAssessment', 'Vendor']),
  'ai.system': () => aiGovernanceApi.util.invalidateTags(['AISystem', 'AIGovernanceDashboard']),
  'audit.workpaper': () => auditApi.util.invalidateTags(['Workpaper']),
  'audit_trail.change': () => auditTrailApi.util.invalidateTags(['ApprovalRequest']),
};

/** Build the action that refreshes the item's own lists after a decision; null for an unknown flow. */
export const invalidateItemTags = (flowKey) => (ITEM_TAGS[flowKey] ? ITEM_TAGS[flowKey]() : null);

// Decide, reassign and cancel return no flow_key, so the caller's arg carries it (optional).
// Without it, refresh every item cache (cheap: tag invalidation only refetches subscribed queries).
const refreshItems = async (arg, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled;
  } catch {
    return;
  }
  const flowKeys = arg?.flowKey ? [arg.flowKey] : Object.keys(ITEM_TAGS);
  flowKeys.forEach((key) => {
    const action = invalidateItemTags(key);
    if (action) dispatch(action);
  });
};

const ROUTED = ['ApprovalInbox', 'ApprovalRequest'];
// A group change can alter built-in flows and the defaults' group keys.
const GROUP_CHANGED = ['ApprovalGroup', 'ApprovalDefaults', 'ApprovalFlow'];

export const approvalsApi = createApi({
  reducerPath: 'approvalsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'ApprovalInbox',
    'ApprovalRequest',
    'ApprovalFlow',
    'ApprovalSettings',
    'ApprovalCover',
    'ApprovalGroup',
    'ApprovalDefaults',
  ],
  endpoints: (builder) => ({
    getApprovalInbox: builder.query({
      query: () => ({ url: `${BASE}/inbox/` }),
      providesTags: ['ApprovalInbox'],
    }),
    getApprovalRequest: builder.query({
      query: ({ flowKey, objectId }) => ({
        url: `${BASE}/requests/`,
        params: { flow_key: flowKey, object_id: objectId },
      }),
      providesTags: ['ApprovalRequest'],
    }),
    decideAssignment: builder.mutation({
      // `payload` only for flows whose approval records details (COI outcome).
      query: ({ id, decision, reason, payload }) => ({
        url: `${BASE}/assignments/${id}/decide/`,
        method: 'POST',
        body: payload ? { decision, reason, payload } : { decision, reason },
      }),
      invalidatesTags: ROUTED,
      onQueryStarted: refreshItems,
    }),
    reassignAssignment: builder.mutation({
      query: ({ id, toUser, reason }) => ({
        url: `${BASE}/assignments/${id}/reassign/`,
        method: 'POST',
        body: { to_user: toUser, reason },
      }),
      invalidatesTags: ROUTED,
      onQueryStarted: refreshItems,
    }),
    // With objectId: people eligible for that item's step. Without (ruling C2-R28): people
    // who may be chosen for flow step `stepOrder` before the item exists -- open to any
    // user who can create the item, so the picker never needs the users list.
    getEligibleApprovers: builder.query({
      query: ({ flowKey, objectId, stepType, stepOrder }) => ({
        url: `${BASE}/eligible/`,
        params: objectId
          ? { flow_key: flowKey, object_id: objectId, step_type: stepType }
          : { flow_key: flowKey, step_order: stepOrder },
      }),
    }),
    getApprovalPreview: builder.query({
      query: ({ flowKey, objectId }) => ({
        url: `${BASE}/preview/`,
        params: { flow_key: flowKey, object_id: objectId },
      }),
    }),
    cancelApprovalRequest: builder.mutation({
      query: ({ id, reason }) => ({
        url: `${BASE}/requests/${id}/cancel/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ROUTED,
      onQueryStarted: refreshItems,
    }),
    getApprovalFlow: builder.query({
      query: (flowKey) => ({ url: `${BASE}/flows/${flowKey}/` }),
      providesTags: (_r, _e, flowKey) => [{ type: 'ApprovalFlow', id: flowKey }],
    }),
    // The built-in flow even when one is saved; used for "Reset to default". Not cached
    // under the flow's tag so it never replaces the saved flow in the editor.
    getApprovalFlowDefault: builder.query({
      query: (flowKey) => ({ url: `${BASE}/flows/${flowKey}/`, params: { default: 1 } }),
      keepUnusedDataFor: 0,
    }),
    updateApprovalFlow: builder.mutation({
      query: ({ flowKey, ...body }) => ({ url: `${BASE}/flows/${flowKey}/`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { flowKey }) => [{ type: 'ApprovalFlow', id: flowKey }],
    }),
    getApprovalSettings: builder.query({
      query: () => ({ url: `${BASE}/settings/` }),
      providesTags: ['ApprovalSettings'],
    }),
    updateApprovalSettings: builder.mutation({
      query: (body) => ({ url: `${BASE}/settings/`, method: 'PUT', body }),
      invalidatesTags: ['ApprovalSettings'],
    }),
    getApprovalCovers: builder.query({
      query: () => ({ url: `${BASE}/covers/` }),
      providesTags: ['ApprovalCover'],
    }),
    createApprovalCover: builder.mutation({
      query: (body) => ({ url: `${BASE}/covers/`, method: 'POST', body }),
      // Creating a cover moves open work to the delegate, so the inbox changes too.
      invalidatesTags: ['ApprovalCover', ...ROUTED],
    }),
    deleteApprovalCover: builder.mutation({
      query: (id) => ({ url: `${BASE}/covers/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['ApprovalCover'],
    }),
    // C2 approval groups. Built-in flows read the groups (an active change board), so a
    // group change also refreshes cached flows.
    getApprovalGroups: builder.query({
      query: () => ({ url: `${BASE}/groups/` }),
      providesTags: ['ApprovalGroup'],
    }),
    createApprovalGroup: builder.mutation({
      query: (body) => ({ url: `${BASE}/groups/`, method: 'POST', body }),
      invalidatesTags: GROUP_CHANGED,
    }),
    // PUT replaces the whole group, members included.
    updateApprovalGroup: builder.mutation({
      query: ({ id, ...body }) => ({ url: `${BASE}/groups/${id}/`, method: 'PUT', body }),
      invalidatesTags: GROUP_CHANGED,
    }),
    // 409 {error} while a flow, an open approval or a default still uses the group.
    deleteApprovalGroup: builder.mutation({
      query: (id) => ({ url: `${BASE}/groups/${id}/`, method: 'DELETE' }),
      invalidatesTags: GROUP_CHANGED,
    }),
    // C2 approval defaults: role map, amount limits, group keys, vendor review days,
    // escalation caps and presets. PUT merges key by key.
    getApprovalDefaults: builder.query({
      query: () => ({ url: `${BASE}/defaults/` }),
      providesTags: ['ApprovalDefaults'],
    }),
    updateApprovalDefaults: builder.mutation({
      query: (body) => ({ url: `${BASE}/defaults/`, method: 'PUT', body }),
      // Built-in flows read the defaults, so cached flows refresh too.
      invalidatesTags: ['ApprovalDefaults', 'ApprovalFlow'],
    }),
  }),
});

export const {
  useGetApprovalInboxQuery,
  useGetApprovalRequestQuery,
  useDecideAssignmentMutation,
  useReassignAssignmentMutation,
  useGetEligibleApproversQuery,
  useGetApprovalPreviewQuery,
  useCancelApprovalRequestMutation,
  useGetApprovalFlowQuery,
  useLazyGetApprovalFlowDefaultQuery,
  useUpdateApprovalFlowMutation,
  useGetApprovalSettingsQuery,
  useUpdateApprovalSettingsMutation,
  useGetApprovalCoversQuery,
  useCreateApprovalCoverMutation,
  useDeleteApprovalCoverMutation,
  useGetApprovalGroupsQuery,
  useCreateApprovalGroupMutation,
  useUpdateApprovalGroupMutation,
  useDeleteApprovalGroupMutation,
  useGetApprovalDefaultsQuery,
  useUpdateApprovalDefaultsMutation,
} = approvalsApi;
